/**
 * Ingest Artifacts Modal
 * Allows users to select artifacts and submit ingest jobs in batches
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { listArtifacts } from '../../api/artifacts';
import { submitIngestJob, submitParseJob } from '../../api/jobs';
import { getProject } from '../../api/projects';
import { getPolicyPack } from '../../api/policy';
import { useWorkspace } from '../../hooks/useWorkspace';
import { RegistryFilterBar } from '../Knowledge/RegistryFilterBar';
import { RegistryMetaBadges } from '../Knowledge/RegistryMetaBadges';
import { RegistryMetaDetailGrid } from '../Knowledge/RegistryMetaDetailGrid';
import { useRegistryArtifactFilters } from '../../hooks/useRegistryArtifactFilters';
import type { Artifact } from '../../types/api';
import {
  Database,
  X,
  Search,
  CheckCircle,
  AlertCircle,
  FileText,
  Loader,
  ChevronDown,
  ChevronRight,
  Info,
  Settings,
} from 'lucide-react';

interface IngestArtifactsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BATCH_SIZE_OPTIONS = [10, 20, 50, 100];
const BATCH_SIZE_MB_OPTIONS = [10, 20, 50, 100, 200, 500]; // MB per batch

type BatchingMode = 'count' | 'size';

export function IngestArtifactsModal({ isOpen, onClose }: IngestArtifactsModalProps) {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();

  const [selectedArtifactIds, setSelectedArtifactIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [batchingMode, setBatchingMode] = useState<BatchingMode>('size'); // Default to size-based
  const [batchSize, setBatchSize] = useState(20); // For count mode
  const [batchSizeMB, setBatchSizeMB] = useState(20); // For size mode (MB) - default 20 MB to prevent timeouts
  const [submittedJobs, setSubmittedJobs] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedArtifact, setExpandedArtifact] = useState<string | null>(null);

  // Job configuration
  const [jobType, setJobType] = useState<'ingest' | 'parse'>('ingest');
  const [indexTypes, setIndexTypes] = useState<string[]>(['evidence', 'narrative']);
  const [parseTables, setParseTables] = useState(true);
  const [parseFigures, setParseFigures] = useState(true);
  const [chunkStrategy, setChunkStrategy] = useState('adaptive');
  const [showConfig, setShowConfig] = useState(false);

  const { data: artifacts, isLoading } = useQuery({
    queryKey: ['artifacts', projectId],
    queryFn: () => listArtifacts(projectId!),
    enabled: !!projectId && isOpen,
  });

  // Fetch project to get policy pack ID
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId && isOpen,
  });

  // Fetch policy pack if available
  const { data: policyPack } = useQuery({
    queryKey: ['policyPack', project?.policy_pack_id],
    queryFn: () => getPolicyPack(project!.policy_pack_id!),
    enabled: !!project?.policy_pack_id && isOpen,
  });

  const filteredArtifacts = useMemo(() => {
    if (!artifacts) return [];

    const artifactArray = Array.isArray(artifacts) ? artifacts : (artifacts as { artifacts?: Artifact[] })?.artifacts || [];

    const searchFiltered = artifactArray.filter((artifact) => {
      const matchesSearch =
        artifact.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artifact.artifact_type?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = filterType === 'all' || artifact.artifact_type === filterType;

      return matchesSearch && matchesType;
    });

    return searchFiltered;
  }, [artifacts, searchTerm, filterType]);

  const {
    filters: registryFilters,
    setFilters: setRegistryFilters,
    groupBy,
    setGroupBy,
    options: registryFilterOptions,
    registryArtifactCount,
    filteredArtifacts: registryFilteredArtifacts,
    clearFilters: clearRegistryFilters,
  } = useRegistryArtifactFilters(filteredArtifacts);

  const displayArtifacts = registryFilteredArtifacts;

  const artifactTypes = useMemo(() => {
    if (!artifacts) return [];
    // Ensure artifacts is an array
    const artifactArray = Array.isArray(artifacts) ? artifacts : (artifacts as any)?.artifacts || [];
    return Array.from(new Set(artifactArray.map((a: any) => a.artifact_type)));
  }, [artifacts]);

  const toggleArtifact = (artifactId: string) => {
    const newSelected = new Set(selectedArtifactIds);
    if (newSelected.has(artifactId)) {
      newSelected.delete(artifactId);
    } else {
      newSelected.add(artifactId);
    }
    setSelectedArtifactIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedArtifactIds.size === displayArtifacts.length) {
      setSelectedArtifactIds(new Set());
    } else {
      setSelectedArtifactIds(new Set(displayArtifacts.map((a) => a.id)));
    }
  };

  // Batch artifacts by size (MB) or count
  // Size-based batching helps prevent Celery timeout (30 min hard limit) by ensuring
  // batches don't exceed reasonable processing time based on total file size
  const batchArtifacts = useMemo(() => {
    if (!artifacts || selectedArtifactIds.size === 0) return [];

    // Ensure artifacts is an array
    const artifactArray = Array.isArray(artifacts) ? artifacts : (artifacts as any)?.artifacts || [];
    const selectedArtifacts = artifactArray.filter((a: any) => selectedArtifactIds.has(a.id));

    if (batchingMode === 'size') {
      // Batch by total MB - groups artifacts until total size approaches limit
      const batches: string[][] = [];
      let currentBatch: string[] = [];
      let currentBatchSizeMB = 0;

      for (const artifact of selectedArtifacts) {
        const artifactSizeMB = (artifact.file_size_bytes || 0) / 1024 / 1024;

        // If adding this artifact would exceed the limit, start a new batch
        // Note: Single artifacts larger than batchSizeMB will still be added (in their own batch)
        if (currentBatch.length > 0 && currentBatchSizeMB + artifactSizeMB > batchSizeMB) {
          batches.push(currentBatch);
          currentBatch = [];
          currentBatchSizeMB = 0;
        }

        currentBatch.push(artifact.id);
        currentBatchSizeMB += artifactSizeMB;
      }

      // Add final batch
      if (currentBatch.length > 0) {
        batches.push(currentBatch);
      }

      return batches;
    } else {
      // Batch by count (original behavior)
      const batches: string[][] = [];
      for (let i = 0; i < selectedArtifacts.length; i += batchSize) {
        batches.push(
          selectedArtifacts
            .slice(i, i + batchSize)
            .map((a: { id: string }) => a.id)
        );
      }
      return batches;
    }
  }, [artifacts, selectedArtifactIds, batchingMode, batchSize, batchSizeMB]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const jobs: string[] = [];

      // Submit in batches (already computed by batchArtifacts)
      for (let i = 0; i < batchArtifacts.length; i++) {
        const batch = batchArtifacts[i];
        try {
          let job;
          if (jobType === 'ingest') {
            job = await submitIngestJob(projectId!, batch, {
              parse_tables: parseTables,
              parse_figures: parseFigures,
              chunk_strategy: chunkStrategy,
              index_types: indexTypes,
            });
          } else {
            // Parse job (if we add it later)
            job = await submitParseJob(projectId!, batch);
          }
          jobs.push(job.id);
        } catch (error) {
          console.error(`Failed to submit batch ${i + 1}:`, error);
          throw error;
        }
      }

      return jobs;
    },
    onSuccess: (jobIds) => {
      setSubmittedJobs(jobIds);
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['artifacts', projectId] });
    },
  });

  const handleSubmit = async () => {
    if (selectedArtifactIds.size === 0) {
      alert('Please select at least one artifact to process');
      return;
    }

    if (jobType === 'ingest' && indexTypes.length === 0) {
      alert('Please select at least one index type for ingest jobs');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitMutation.mutateAsync();
    } catch (error) {
      console.error('Failed to submit jobs:', error);
      alert(`Failed to submit ${jobType} jobs. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedArtifactIds(new Set());
      setSearchTerm('');
      setFilterType('all');
      setSubmittedJobs([]);
      onClose();
    }
  };

  // Auto-close modal 5 seconds after successful submission
  useEffect(() => {
    if (submittedJobs.length > 0) {
      const timer = setTimeout(() => {
        setSelectedArtifactIds(new Set());
        setSearchTerm('');
        setFilterType('all');
        setSubmittedJobs([]);
        onClose();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [submittedJobs.length, onClose]);

  if (!isOpen) return null;

  const totalBatches = batchArtifacts.length;
  const totalSizeMB = (Array.isArray(artifacts) ? artifacts : (artifacts as any)?.artifacts || []).filter((a: any) => selectedArtifactIds.has(a.id)).reduce((sum: number, a: any) => sum + (a.file_size_bytes || 0) / 1024 / 1024, 0) || 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleClose}>
      <div className="max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <Card className="flex flex-col h-full max-h-[90vh] bg-white dark:bg-black/60 overflow-hidden" padding="md">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b flex-shrink-0">
            <div className="flex items-center gap-3">
              <Database className="h-6 w-6 text-blue-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Ingest Artifacts</h2>
                <p className="text-sm text-gray-600">
                  {jobType === 'ingest'
                    ? 'Select artifacts to ingest (parse + chunk + index + extract entities)'
                    : 'Select artifacts to parse only (extract content, tables, figures)'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Success State */}
          {submittedJobs.length > 0 && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-green-900 mb-1">Ingest Jobs Submitted!</h4>
                  <p className="text-sm text-green-700">
                    Successfully submitted {submittedJobs.length} job{submittedJobs.length !== 1 ? 's' : ''} for {selectedArtifactIds.size} artifact{selectedArtifactIds.size !== 1 ? 's' : ''}.
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    Jobs are being processed. You can monitor progress in the Jobs list.
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    This modal will close automatically in a few seconds...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Job Type and Configuration */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-blue-900">Job Configuration</h3>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowConfig(!showConfig)}
              >
                {showConfig ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {showConfig ? 'Hide' : 'Show'} Configuration
              </Button>
            </div>

            {showConfig && (
              <div className="space-y-4 mt-3">
                {/* Job Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Type
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="jobType"
                        value="ingest"
                        checked={jobType === 'ingest'}
                        onChange={(e) => setJobType(e.target.value as 'ingest' | 'parse')}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-blue-900">
                        <Database className="h-4 w-4 inline mr-1" />
                        Ingest (Parse + Chunk + Index + Entities)
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="jobType"
                        value="parse"
                        checked={jobType === 'parse'}
                        onChange={(e) => setJobType(e.target.value as 'ingest' | 'parse')}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-blue-900">
                        <FileText className="h-4 w-4 inline mr-1" />
                        Parse Only
                      </span>
                    </label>
                  </div>
                </div>

                {/* Index Types (only for ingest) */}
                {jobType === 'ingest' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Index Types (where chunks will be grouped)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['evidence', 'narrative', 'compliance', 'technical'].map((type) => (
                        <label key={type} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={indexTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setIndexTypes([...indexTypes, type]);
                              } else {
                                setIndexTypes(indexTypes.filter(t => t !== type));
                              }
                            }}
                            className="text-blue-600"
                          />
                          <Badge variant="default">{type}</Badge>
                        </label>
                      ))}
                    </div>
                    {indexTypes.length === 0 && (
                      <p className="text-xs text-red-600 mt-1">At least one index type must be selected</p>
                    )}
                  </div>
                )}

                {/* Additional Config */}
                {jobType === 'ingest' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chunk Strategy
                      </label>
                      <select
                        value={chunkStrategy}
                        onChange={(e) => setChunkStrategy(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      >
                        <option value="adaptive">Adaptive</option>
                        <option value="fixed">Fixed Size</option>
                        <option value="semantic">Semantic</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={parseTables}
                          onChange={(e) => setParseTables(e.target.checked)}
                          className="text-blue-600"
                        />
                        <span className="text-sm text-blue-900">Parse Tables</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={parseFigures}
                          onChange={(e) => setParseFigures(e.target.checked)}
                          className="text-blue-600"
                        />
                        <span className="text-sm text-blue-900">Parse Figures</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Policy Pack Info */}
                {policyPack && (
                  <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-900">Policy Pack Configuration</span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div><strong>Name:</strong> {policyPack.name}</div>
                      <div><strong>Version:</strong> {policyPack.version}</div>
                      {policyPack.description && (
                        <div><strong>Description:</strong> {policyPack.description}</div>
                      )}
                      {policyPack.rules?.entity_extraction && (
                        <div className="mt-2">
                          <strong>Entity Types:</strong>{' '}
                          {Object.keys(policyPack.rules.entity_extraction.prompts || {}).join(', ') || 'None configured'}
                        </div>
                      )}
                      {policyPack.rules?.entity_extraction?.enabled !== undefined && (
                        <div className="mt-1">
                          <strong>Entity Extraction:</strong>{' '}
                          <Badge variant={policyPack.rules.entity_extraction.enabled ? 'success' : 'default'}>
                            {policyPack.rules.entity_extraction.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Filters and Selection Controls */}
          <div className="space-y-4 mb-4 flex-shrink-0">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search artifacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftIcon={<Search className="h-4 w-4 text-gray-400" />}
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {artifactTypes.map((type: any) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {registryArtifactCount > 0 && (
              <RegistryFilterBar
                filters={registryFilters}
                options={registryFilterOptions}
                groupBy={groupBy}
                registryArtifactCount={registryArtifactCount}
                onFiltersChange={setRegistryFilters}
                onGroupByChange={setGroupBy}
                onClear={clearRegistryFilters}
              />
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleSelectAll}
                  disabled={displayArtifacts.length === 0}
                >
                  {selectedArtifactIds.size === displayArtifacts.length ? 'Deselect All' : 'Select All'}
                </Button>
                <span className="text-sm text-gray-600">
                  {selectedArtifactIds.size} of {displayArtifacts.length} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Batching Mode:</label>
                <select
                  value={batchingMode}
                  onChange={(e) => setBatchingMode(e.target.value as BatchingMode)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitting}
                >
                  <option value="size">By Size (MB)</option>
                  <option value="count">By Count</option>
                </select>
                {batchingMode === 'size' ? (
                  <>
                    <select
                      value={batchSizeMB}
                      onChange={(e) => setBatchSizeMB(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      disabled={isSubmitting}
                    >
                      {BATCH_SIZE_MB_OPTIONS.map(size => (
                        <option key={size} value={size}>{size} MB</option>
                      ))}
                    </select>
                    {selectedArtifactIds.size > 0 && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({totalBatches} batch{totalBatches !== 1 ? 'es' : ''}, {totalSizeMB.toFixed(1)} MB total)
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <select
                      value={batchSize}
                      onChange={(e) => setBatchSize(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      disabled={isSubmitting}
                    >
                      {BATCH_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size} files</option>
                      ))}
                    </select>
                    {selectedArtifactIds.size > 0 && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({totalBatches} batch{totalBatches !== 1 ? 'es' : ''})
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Artifacts List - Scrollable */}
          <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="md" />
                <span className="ml-2 text-gray-600">Loading artifacts...</span>
              </div>
            ) : displayArtifacts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>No artifacts found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {displayArtifacts.map((artifact) => {
                  const isSelected = selectedArtifactIds.has(artifact.id);
                  const isExpanded = expandedArtifact === artifact.id;
                  const sizeMB = ((artifact.file_size_bytes || 0) / 1024 / 1024).toFixed(2);

                  return (
                    <div
                      key={artifact.id}
                      className={`p-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleArtifact(artifact.id)}
                          disabled={isSubmitting}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <button
                              onClick={() => setExpandedArtifact(isExpanded ? null : artifact.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                            <span className={`font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{artifact.filename}</span>
                            <Badge>{artifact.artifact_type}</Badge>
                            <span className="text-xs text-gray-500">{sizeMB} MB</span>
                          </div>
                          <RegistryMetaBadges artifact={artifact} compact />

                          {isExpanded && (
                            <div className="ml-7 mt-2 space-y-2">
                              <div className="text-xs text-gray-600 space-y-1">
                                <div>ID: {artifact.id.substring(0, 24)}...</div>
                                <div>Source: {artifact.source_type || 'upload'}</div>
                                <div>Created: {new Date(artifact.created_at).toLocaleString()}</div>
                              </div>
                              <RegistryMetaDetailGrid artifact={artifact} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Job Summary */}
          {selectedArtifactIds.size > 0 && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex-shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-900">Job Summary</span>
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div><strong>Job Type:</strong> {jobType === 'ingest' ? 'Ingest (Parse + Chunk + Index + Extract Entities)' : 'Parse Only'}</div>
                {jobType === 'ingest' && (
                  <>
                    <div><strong>Index Types:</strong> {indexTypes.join(', ') || 'None selected'}</div>
                    <div><strong>Chunk Strategy:</strong> {chunkStrategy}</div>
                    <div><strong>Parse Tables:</strong> {parseTables ? 'Yes' : 'No'}</div>
                    <div><strong>Parse Figures:</strong> {parseFigures ? 'Yes' : 'No'}</div>
                  </>
                )}
                <div><strong>Artifacts:</strong> {selectedArtifactIds.size} selected ({totalSizeMB.toFixed(1)} MB total)</div>
                <div><strong>Batches:</strong> {totalBatches} batch{totalBatches !== 1 ? 'es' : ''} ({batchingMode === 'size' ? `max ${batchSizeMB} MB each` : `${batchSize} files each`})</div>
                {policyPack && (
                  <div className="mt-2 pt-2 border-t border-gray-300">
                    <div><strong>Policy Pack:</strong> {policyPack.name} v{policyPack.version}</div>
                    {policyPack.rules?.entity_extraction?.enabled && (
                      <div><strong>Entity Extraction:</strong> Enabled ({Object.keys(policyPack.rules.entity_extraction.prompts || {}).length} types configured)</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer Actions - Always Visible */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t flex-shrink-0 bg-white">
            <div className="text-sm text-gray-600">
              {selectedArtifactIds.size > 0 && (
                <>
                  {selectedArtifactIds.size} artifact{selectedArtifactIds.size !== 1 ? 's' : ''} selected ({totalSizeMB.toFixed(1)} MB)
                  {totalBatches > 1 && ` • ${totalBatches} batch${totalBatches !== 1 ? 'es' : ''} will be created`}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {submittedJobs.length > 0 ? 'Close' : 'Cancel'}
              </Button>
              {submittedJobs.length === 0 && (
                <Button
                  onClick={handleSubmit}
                  disabled={selectedArtifactIds.size === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4 mr-2" />
                      {jobType === 'ingest'
                        ? `Create Chunks and Build Entity Tables (${indexTypes.join(', ')})`
                        : 'Parse Artifacts Only'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

